import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import DesignerWrapper from "shared/components/designer/wrapper";
import ClientWrapper from "shared/components/client/wrapper";
import CollaboratorWrapper from "shared/components/collaborator/wrapper";
import AuthLayout from "pages/auth/layout";
import { routeConstant } from "./routeConstant";
import { Images } from "assets";
import BuilderWrapper from "shared/components/builder/wrapper";
import { GetUserProfileService } from "shared/services/user";
import { setAuthReducer } from "shared/redux/reducers/authSlice";
import { handleErrors } from "shared/utils/helper";
import { onboardingProjectsToEnter } from "shared/utils/pendingOnboarding";

interface LayoutProps {
  path: string;
  title: string;
  Component: any;
}

const Layout = ({ title, Component }: Partial<LayoutProps>) => {
  const {
    auth: { isLoggedIn },
  } = useSelector((state: any) => state.root);
  const userType = useSelector((state: any) => state.root.auth.user?.user_type);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isCheckingOnboarding = useRef(false);

  useEffect(() => {
    document.title = title + " | Tracy's Home Designs";
  });

  useEffect(() => {
    if (isLoggedIn && userType) {
      const userTypeRoutes: { [key: string]: string } = {
        client: "/client",
        designer: "/designer",
        builder: "/builder",
        collaborator: "/collaborator",
      };

      const defaultRoutes: { [key: string]: string } = {
        client: routeConstant.client.projects.path,
        designer: routeConstant.designer.home.path,
        builder: routeConstant.builder.projects.path,
        collaborator: routeConstant.collaborator.projects.path,
      };

      const expectedPrefix = userTypeRoutes[userType];
      const isAuthRoute = location.pathname.includes("/auth/");

      if (!isAuthRoute && expectedPrefix) {
        const isAccessingOtherUserTypeRoute = Object.values(
          userTypeRoutes,
        ).some(
          (prefix) =>
            prefix !== expectedPrefix && location.pathname.startsWith(prefix),
        );

        if (isAccessingOtherUserTypeRoute) {
          navigate(defaultRoutes[userType] || defaultRoutes.designer, {
            replace: true,
          });
        }
      }
    }
  }, [isLoggedIn, userType, location.pathname, navigate]);

  useEffect(() => {
    const checkProjectOnboarding = async () => {
      if (userType !== "client" || !isLoggedIn) {
        return;
      }

      if (location.pathname.includes("/auth/onboarding")) {
        return;
      }

      if (isCheckingOnboarding.current) {
        return;
      }

      try {
        isCheckingOnboarding.current = true;

        const response = await GetUserProfileService();

        if (response?.data) {
          const profileData = response.data.data || response.data;

          const onboardingProjects = Array.isArray(
            profileData?.onboarding_projects,
          )
            ? profileData.onboarding_projects
            : Array.isArray(response.data?.onboarding_projects)
              ? response.data.onboarding_projects
              : [];

          // Same gate the dashboard wrapper uses, so a dismissed onboarding
          // is not immediately re-entered from here.
          const pendingOnboarding = onboardingProjectsToEnter({
            pathname: location.pathname,
            list: onboardingProjects,
          });

          if (pendingOnboarding.length > 0) {
            dispatch(
              setAuthReducer({
                onboarding_projects: pendingOnboarding,
                onboarding_project: pendingOnboarding[0],
              }),
            );

            // Enter the onboarding flow for the first pending project; the
            // flow advances through any remaining ones after each submit.
            navigate(routeConstant.onboarding.path, {
              replace: true,
              state: { projectId: pendingOnboarding[0].project_id },
            });
          }
        }
      } catch (error) {
        handleErrors(error);
      } finally {
        isCheckingOnboarding.current = false;
      }
    };

    checkProjectOnboarding();
  }, [isLoggedIn, userType, location.pathname, navigate, dispatch]);

  const getAuthImage = () => {
    if (location.pathname.includes("/forgot")) {
      return Images.ForgotImg;
    }
    if (
      location.pathname.includes("/reset-password") ||
      location.pathname.includes("/set-password")
    ) {
      return Images.ResetPasswordImg;
    }
    if (location.pathname.includes("/login")) {
      return Images.LoginImg;
    }
    return undefined;
  };

  if (location.pathname.includes("auth/onboarding")) {
    return <Component />;
  }

  if (
    !isLoggedIn &&
    (location.pathname === routeConstant.login.path ||
      location.pathname === routeConstant.forgotPassword.path ||
      location.pathname === routeConstant.resetPassword.path ||
      location.pathname.includes("/auth/"))
  ) {
    return (
      <AuthLayout image={getAuthImage()}>
        <Component />
      </AuthLayout>
    );
  }

  if (location.pathname.includes("/client")) {
    return (
      <ClientWrapper>
        <Component />
      </ClientWrapper>
    );
  }

  if (location.pathname.includes("/collaborator")) {
    return (
      <CollaboratorWrapper>
        <Component />
      </CollaboratorWrapper>
    );
  }

  if (location.pathname.includes("/builder")) {
    return (
      <BuilderWrapper>
        <Component />
      </BuilderWrapper>
    );
  }

  return (
    <DesignerWrapper>
      <Component />
    </DesignerWrapper>
  );
};

export default Layout;
